import apiClient from './api';

export interface AgentMessage {
  role: 'user' | 'model';
  content: string;
  type: 'text' | 'resource' | 'error';
  created_at?: number;
  resource_ids?: string[];
}

export interface VideoAgentSession {
  session_id: string;
  status: string;
  video_id?: string;
  progress?: number;
  messages: AgentMessage[];
  narration_script?: string;
  s3_video_url?: string;
}

export interface RevisionResult {
  success: boolean;
  s3_video_url?: string;
  duration_seconds?: number;
  error?: string;
  // Async (fire-and-suspend): the render was dispatched and will be finalized
  // off-worker. When true, poll getVideoStatus() until completion for the URL.
  dispatched?: boolean;
  status?: 'processing' | 'completed';
}

export interface VideoStatusResult {
  // Async avatar render lifecycle: queued | dispatched | agent_processing | awaiting_approval
  // | agent_approved | video_rendering | ready_to_finalize | completed | failed
  avatar_render_status?: string;
  avatar_render_error?: string;
  s3_video_url?: string;
}

export interface DraftResource {
  resource_id: string;
  resource_type?: string; // image | video | draft | ...
  url?: string;
  thumbnail_url?: string;
  preview_url?: string;
}

export interface DraftResult {
  session_id: string;
  status: string;            // HeyGen session: thinking|waiting_for_input|reviewing|generating|completed|failed
  progress?: number;         // 0-100
  avatar_render_status?: string; // our lifecycle (incl. awaiting_approval)
  s3_video_url?: string;
  messages: AgentMessage[];
  resources: DraftResource[];
}

export const avatarVideoService = {
  /**
   * Get Video Agent session details (conversation + storyboard)
   */
  async getSession(content_item_id: string): Promise<VideoAgentSession> {
    try {
      const response = await apiClient.get<any>(
        `/api/avatar-models/video-session/${content_item_id}`
      );
      return response.data?.data || {
        session_id: '',
        status: 'error',
        messages: [],
      };
    } catch (error) {
      console.error('Failed to fetch video agent session:', error);
      return { session_id: '', status: 'error', messages: [] };
    }
  },

  /**
   * Revise avatar video (scene-level edit)
   * Cost: ~$0.50-1
   */
  async revise(content_item_id: string, message: string): Promise<RevisionResult> {
    try {
      const response = await apiClient.post<any>(
        `/api/avatar-models/video-session/${content_item_id}/revise`,
        { message }
      );
      return response.data?.data || { success: false, error: 'No data returned' };
    } catch (error: any) {
      console.error('Failed to revise avatar video:', error);
      return {
        success: false,
        error: error.response?.data?.error?.error_description || error.message || 'Revision failed',
      };
    }
  },

  /**
   * Full regeneration with new narration
   * Cost: ~$3-5
   */
  async regenerate(
    content_item_id: string,
    narration_script: string,
    avatar_config?: any
  ): Promise<RevisionResult> {
    try {
      const response = await apiClient.post<any>(
        `/api/avatar-models/video-session/${content_item_id}/regenerate`,
        { narration_script, avatar_config }
      );
      return response.data?.data || { success: false, error: 'No data returned' };
    } catch (error: any) {
      console.error('Failed to regenerate avatar video:', error);
      return {
        success: false,
        error: error.response?.data?.error?.error_description || error.message || 'Regeneration failed',
      };
    }
  },

  /**
   * Lightweight status read for an async avatar render (used while polling after
   * revise/regenerate). Unwraps the nested ResultEntity → service { success, data }.
   */
  async getVideoStatus(content_item_id: string): Promise<VideoStatusResult> {
    try {
      const response = await apiClient.get<any>(`/course/content-item/${content_item_id}/video-status`);
      const body = response.data?.data;          // ResultEntity payload (the service result)
      const inner = body?.data ?? body;          // service { data: {...} } → actual content-item status
      return {
        avatar_render_status: inner?.avatar_render?.status,
        avatar_render_error: inner?.avatar_render?.error,
        s3_video_url: inner?.media_assets?.s3_video_url,
      };
    } catch (error) {
      console.error('Failed to fetch content item video status:', error);
      return {};
    }
  },

  /**
   * Poll getVideoStatus until the async avatar render reaches a terminal state.
   * Resolves with the final S3 video URL on success, or throws with the error.
   *
   * @param onTick optional progress callback (receives the current avatar_render_status)
   */
  async pollUntilComplete(
    content_item_id: string,
    opts?: { intervalMs?: number; timeoutMs?: number; onTick?: (status?: string) => void }
  ): Promise<{ s3_video_url: string }> {
    const intervalMs = opts?.intervalMs ?? 5000;
    // 6h ceiling: under the shared-account concurrency gate an item can stay QUEUED for
    // hours behind a create batch before its render even starts.
    const timeoutMs = opts?.timeoutMs ?? 6 * 60 * 60 * 1000;
    const started = Date.now();

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const status = await this.getVideoStatus(content_item_id);
      opts?.onTick?.(status.avatar_render_status);

      if (status.avatar_render_status === 'completed') {
        if (status.s3_video_url) return { s3_video_url: status.s3_video_url };
        // completed but URL not yet visible — give the next tick a chance
      } else if (status.avatar_render_status === 'failed') {
        throw new Error(status.avatar_render_error || 'Avatar render failed');
      }

      if (Date.now() - started > timeoutMs) {
        // Not a failure — the render is still progressing off-worker. Surface a soft,
        // typed timeout so the UI shows an informational "safe to close" message.
        const e: any = new Error('Still rendering in the background — you can close this dialog and check back later.');
        e.code = 'POLL_TIMEOUT';
        throw e;
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  },

  /** Fetch the current chat-mode draft (storyboard messages + resolved preview resources). */
  async getDraft(content_item_id: string): Promise<DraftResult> {
    try {
      const response = await apiClient.get<any>(`/api/avatar-models/video-session/${content_item_id}/draft`);
      const body = response.data?.data ?? response.data;
      return {
        session_id: body?.session_id || '',
        status: body?.status || 'unknown',
        progress: body?.progress,
        avatar_render_status: body?.avatar_render_status,
        s3_video_url: body?.s3_video_url,
        messages: Array.isArray(body?.messages) ? body.messages : [],
        resources: Array.isArray(body?.resources) ? body.resources : [],
      };
    } catch (error) {
      console.error('Failed to fetch draft:', error);
      return { session_id: '', status: 'error', messages: [], resources: [] };
    }
  },

  /** Approve the paused draft → start the final render. */
  async approveDraft(content_item_id: string): Promise<RevisionResult> {
    try {
      const response = await apiClient.post<any>(`/api/avatar-models/video-session/${content_item_id}/approve-draft`, {});
      return response.data?.data || { success: true, dispatched: true, status: 'processing' };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error?.error_description || error.message || 'Approve failed' };
    }
  },

  /** Request changes on the paused draft → agent re-drafts for another review. */
  async requestChanges(content_item_id: string, message: string): Promise<RevisionResult> {
    try {
      const response = await apiClient.post<any>(`/api/avatar-models/video-session/${content_item_id}/request-changes`, { message });
      return response.data?.data || { success: true, dispatched: true, status: 'processing' };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.error?.error_description || error.message || 'Request changes failed' };
    }
  },

  /**
   * Poll the draft until it either pauses for human review (awaiting_approval), fails,
   * or completes. Resolves with a phase so the modal can branch. onTick reports progress.
   */
  async pollUntilDraftOrDone(
    content_item_id: string,
    opts?: { intervalMs?: number; timeoutMs?: number; onTick?: (draft: DraftResult) => void }
  ): Promise<{ phase: 'awaiting_approval' | 'completed' | 'failed'; draft: DraftResult }> {
    const intervalMs = opts?.intervalMs ?? 5000;
    const timeoutMs = opts?.timeoutMs ?? 6 * 60 * 60 * 1000;
    const started = Date.now();
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const draft = await this.getDraft(content_item_id);
      opts?.onTick?.(draft);
      const s = draft.avatar_render_status;
      if (s === 'awaiting_approval') return { phase: 'awaiting_approval', draft };
      if (s === 'completed') return { phase: 'completed', draft };
      if (s === 'failed') return { phase: 'failed', draft };
      if (Date.now() - started > timeoutMs) {
        const e: any = new Error('Still working in the background — you can close this and check back later.');
        e.code = 'POLL_TIMEOUT';
        throw e;
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  },
};
